import React, { useState } from 'react';
import Tooltip from '../common/Tooltip';
import { useRiskMetrics } from '../../hooks/useRiskMetrics';
import { useAppSelector } from '../../store/hooks';

type RiskRange = '30d' | '90d' | '1y';

const RANGE_OPTIONS: { value: RiskRange; label: string }[] = [
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '1y', label: '1y' },
];

const METRIC_TOOLTIPS = {
  sharpe:
    'Sharpe Ratio: annualized excess return per unit of total volatility. Above 1 is good; above 2 is excellent; below 0 is poor.',
  sortino:
    'Sortino Ratio: like Sharpe but only penalises downside volatility. Rewards consistent gains even when the portfolio has occasional spikes.',
  maxDrawdown:
    'Max Drawdown: the largest peak-to-trough decline (%) over the period. Lower is better — it measures worst-case loss from a high point.',
  calmar:
    'Calmar Ratio: annualized return divided by max drawdown. Higher means better risk-adjusted return relative to the worst loss experienced.',
};

interface MetricCellProps {
  label: string;
  tooltip: string;
  value: number | null;
  isLoading: boolean;
  formatValue: (v: number) => string;
  colorFn: (v: number) => string;
  suffix?: string;
}

function MetricCell({
  label,
  tooltip,
  value,
  isLoading,
  formatValue,
  colorFn,
  suffix = '',
}: MetricCellProps) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 md:p-5">
      <p className="text-xs uppercase text-text-muted tracking-wider mb-2 flex items-center gap-1">
        {label}
        <Tooltip content={tooltip} ariaLabel={`${label} explanation`} />
      </p>
      {isLoading ? (
        <div className="skeleton h-8 w-24 rounded" />
      ) : value == null ? (
        <p className="text-2xl font-mono font-bold tabular-nums text-gray-500">—</p>
      ) : (
        <p className={`text-2xl font-mono font-bold tabular-nums ${colorFn(value)}`}>
          {formatValue(value)}
          {suffix}
        </p>
      )}
    </div>
  );
}

interface RiskMetricsPanelProps {
  /** Override initial range (optional) */
  defaultRange?: RiskRange;
}

const RiskMetricsPanel: React.FC<RiskMetricsPanelProps> = ({ defaultRange = '30d' }) => {
  const [range, setRange] = useState<RiskRange>(defaultRange);
  const { user } = useAppSelector((s) => s.auth);
  const { data, isLoading, error } = useRiskMetrics(user?.id, range);

  const { sharpeRatio, sortinoRatio, maxDrawdown, calmarRatio } = data ?? {
    sharpeRatio: null,
    sortinoRatio: null,
    maxDrawdown: null,
    calmarRatio: null,
  };

  const ratioColor = (v: number) =>
    v >= 2 ? 'text-emerald-400' : v >= 1 ? 'text-amber-400' : v >= 0 ? 'text-yellow-500' : 'text-red-400';

  const drawdownColor = (_v: number) => 'text-red-400';

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Risk-Adjusted Metrics</h2>

        {/* Range selector */}
        <div
          role="tablist"
          aria-label="Select time range for risk metrics"
          className="flex items-center gap-1"
        >
          {RANGE_OPTIONS.map((opt) => {
            const isActive = opt.value === range;
            return (
              <button
                key={opt.value}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setRange(opt.value)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Error */}
      {error && (
        <p className="text-red-400 text-sm mb-3">{error}</p>
      )}

      {/* 2×2 grid */}
      <div className="grid grid-cols-2 gap-4">
        <MetricCell
          label="Sharpe Ratio"
          tooltip={METRIC_TOOLTIPS.sharpe}
          value={sharpeRatio}
          isLoading={isLoading}
          formatValue={(v) => (v >= 0 ? '+' : '') + v.toFixed(2)}
          colorFn={ratioColor}
        />
        <MetricCell
          label="Sortino Ratio"
          tooltip={METRIC_TOOLTIPS.sortino}
          value={sortinoRatio}
          isLoading={isLoading}
          formatValue={(v) => (v >= 0 ? '+' : '') + v.toFixed(2)}
          colorFn={ratioColor}
        />
        <MetricCell
          label="Max Drawdown"
          tooltip={METRIC_TOOLTIPS.maxDrawdown}
          value={maxDrawdown != null ? -maxDrawdown : null}
          isLoading={isLoading}
          formatValue={(v) => v.toFixed(2)}
          colorFn={drawdownColor}
          suffix="%"
        />
        <MetricCell
          label="Calmar Ratio"
          tooltip={METRIC_TOOLTIPS.calmar}
          value={calmarRatio}
          isLoading={isLoading}
          formatValue={(v) => (v >= 0 ? '+' : '') + v.toFixed(2)}
          colorFn={ratioColor}
        />
      </div>
    </div>
  );
};

export default RiskMetricsPanel;
